import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip
} from "chart.js"

import "chartjs-adapter-moment"

import { formatKpiValue } from "../../../Components/KpiTabs/formatKpiValue"
import { INCOGNITO_MASK } from "../../../Components/Private"
import { isIncognitoActive } from "../../../Utils/incognito"
import { baseOptions } from "../../DashboardPage/components/chartsOptions"
import { getGradient } from "../../DashboardPage/components/chartsPlagins"
import { kpiTooltip } from "../../DashboardPage/components/chartTooltips"
import { CHANNEL_COLORS, channelLabel } from "./vocabulary"

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const LINE_COLOR = "#225AEA"
const COMPARISON_LINE_COLOR = "#A1A1AA"

// The comparison overlay is a dashed grey line with its own ringed point on hover (RCA-5-08).
export const COMPARISON_LINE_STYLE = {
  borderColor: COMPARISON_LINE_COLOR,
  pointHoverRadius: 6,
  pointHoverBorderWidth: 2,
  pointHoverBorderColor: COMPARISON_LINE_COLOR,
  pointHoverBackgroundColor: "white"
}

// A rate's axis stays inside 0–100 on multiples of ten (RCA-5-06); chart.js would otherwise pad
// a 98% peak up to 105%.
const clampPercentAxis = (scale) => {
  const max = Math.min(100, Math.ceil(scale.max / 10) * 10)
  const min = Math.max(0, Math.floor(scale.min / 10) * 10)
  scale.max = max > min ? max : Math.min(100, min + 10)
  scale.min = max > min ? min : scale.max - 10
}

// The metrics whose channel rows add up to the headline (RCA-5-10): a count and a money total.
// An average or a rate is each channel's own figure and reconciles with nothing.
const SUMMED_METRICS = new Set(["lookups", "order_amount"])

const CHANNELS = Object.keys(CHANNEL_COLORS)

// Each channel's value for one bucket. In a summed metric the values are rounded the way the
// headline is (whole dollars, whole lookups) and the last channel with a value takes the
// difference, so the rows always add up to the Total above them. Should that row go negative
// (several rows rounded up past it) the bucket is split by largest remainder instead.
export const channelRowValues = ({ values, dataIndex, total, summed }) => {
  const raw = CHANNELS.map((channel) => values?.[channel]?.[dataIndex] ?? null)

  if (!summed) return raw

  const target = Math.round(total ?? 0)
  const rounded = raw.map((value) => Math.round(value ?? 0))
  const last = raw.findLastIndex((value) => value)

  if (last < 0) return rounded

  rounded[last] = target - rounded.reduce((sum, value, i) => (i === last ? sum : sum + value), 0)

  if (rounded[last] >= 0) return rounded

  const floors = raw.map((value) => Math.floor(value ?? 0))
  let left = target - floors.reduce((sum, value) => sum + value, 0)
  const byRemainder = raw
    .map((value, i) => ({ i, remainder: (value ?? 0) - floors[i] }))
    .sort((a, b) => b.remainder - a.remainder)

  for (const { i } of byRemainder) {
    if (left <= 0) break
    floors[i] += 1
    left -= 1
  }

  return floors
}

// The tooltip's channel rows (RCA-5-07): the channel's colour marker, its name and its value. A
// channel with no lookups has no average or rate, so its row reads a dash.
const channelRows =
  ({ t, format, values, totals, summed }) =>
  (dataIndex) => {
    if (!values) return []

    const rowValues = channelRowValues({ values, dataIndex, total: totals?.[dataIndex], summed })

    return CHANNELS.map((channel, i) => ({
      icon: `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;flex-shrink:0;background:${CHANNEL_COLORS[channel]};"></span>`,
      label: t(channelLabel(channel)),
      value: isIncognitoActive()
        ? INCOGNITO_MASK
        : rowValues[i] == null
          ? "—"
          : format(rowValues[i])
    }))
  }

// Options for the KPI trend line under the tabs. `title` is the selected tab's own label —
// the chart has no visible title, but the tooltip leads with it, which is the only place
// the reader is told which metric they are hovering.
export const kpiTrendOptions = ({
  t,
  theme,
  metric,
  title,
  prefix,
  suffix,
  decimals,
  totalLabel,
  totals,
  breakdown,
  comparisonTotals,
  comparisonBreakdown
}) => {
  const base = baseOptions(theme)
  const format = formatKpiValue({ prefix, suffix, decimals })
  const isRate = suffix === "%"
  // The axis stays whole even where the figure has decimals.
  const formatTick = formatKpiValue({ prefix, suffix })
  // Drawn by chart.js outside React, hence the live Incognito mirror rather than <Private>.
  const formatValue = (value) => (isIncognitoActive() ? INCOGNITO_MASK : formatTick(value))

  const summed = SUMMED_METRICS.has(metric)

  return {
    ...base,
    interaction: { mode: "index", intersect: false },
    // Every series at the hovered bucket gets its ringed point, the comparison line's too (RCA-5-07).
    hover: { ...base.hover, mode: "index", intersect: false },
    plugins: {
      "vertical-line-plugin": { theme },
      title: { display: false },
      legend: { display: false },
      tooltip: kpiTooltip({
        theme,
        label: title,
        formatValue: format,
        totalLabel,
        rows: channelRows({ t, format, values: breakdown, totals, summed }),
        comparisonRows: channelRows({
          t,
          format,
          values: comparisonBreakdown,
          totals: comparisonTotals,
          summed
        })
      })
    },
    scales: {
      ...base.scales,
      x: {
        ...base.scales.x,
        ticks: { ...base.scales.x.ticks, maxTicksLimit: 10, autoSkip: true, maxRotation: 0 }
      },
      y: {
        ...base.scales.y,
        ...(isRate ? { afterDataLimits: clampPercentAxis } : {}),
        // Counts and percentages are whole; a fractional tick prints the same label twice.
        ticks: { ...base.scales.y.ticks, precision: 0, callback: (value) => formatValue(value) }
      }
    }
  }
}

export const buildKpiDataset = ({ label, data, pointCount }) => ({
  label,
  data,
  borderColor: LINE_COLOR,
  borderWidth: 2,
  yAxisID: "y",
  // Win rate is null in buckets with nothing resolved; span those gaps so the line stays
  // continuous instead of fragmenting. Count series carry no nulls, so this is a no-op there.
  spanGaps: true,
  fill: true,
  backgroundColor: (context) => {
    const { ctx, chartArea } = context.chart
    if (!chartArea) return undefined
    return getGradient(ctx, chartArea)
  },
  ...(pointCount === 1
    ? { pointRadius: 4, pointHoverRadius: 6 }
    : { pointRadius: 0, pointHoverRadius: 6 }),
  pointHoverBorderWidth: 2,
  pointHoverBackgroundColor: "white"
})
